import { Link } from "react-router-dom";
import { AlertTriangle, Radio, Globe, CheckCircle2 } from "lucide-react";
import { usePortalReadiness, type PortalReadiness } from "@/hooks/usePortals.js";
import { Permalink } from "@/components/Permalink.js";
import { env } from "@/env.js";

const API_BASE = env.VITE_API_BASE_URL;

export default function Portals(): JSX.Element {
  const { data, isLoading } = usePortalReadiness();
  const portals = data?.portals ?? [];
  const esteri = portals.filter((p) => p.foreign).length;

  return (
    <div className="space-y-[22px]">
      <header>
        <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Portali</h1>
        <p className="mt-[5px] text-[13.5px] text-ink-soft">
          Una fonte, un feed per portale. Dai a ogni portale il suo URL: pubblichi gli immobili una volta sola.
        </p>
      </header>

      <div className="grid grid-cols-2 overflow-hidden rounded-[5px] border border-rule bg-surface sm:grid-cols-4">
        <Stat v={String(data?.published ?? 0)} l="Pubblicati" />
        <Stat v={String(portals.length)} l="Portali configurati" />
        <Stat v={String(portals.length - esteri)} l="Italiani" />
        <Stat v={String(esteri)} l="Esteri" last />
      </div>

      {isLoading ? (
        <p className="micro">Calcolo readiness…</p>
      ) : (data?.published ?? 0) === 0 ? (
        <div className="ticks relative rounded-[5px] border border-rule bg-surface p-[40px_24px] text-center">
          <p className="text-[14px] font-semibold text-ink">Nessun immobile pubblicato</p>
          <p className="micro mt-2">Pubblica almeno un immobile per generare i feed dei portali.</p>
        </div>
      ) : (
        portals.map((p) => <PortalCard key={p.key} p={p} />)
      )}

      <p className="micro text-center">
        I feed sono XML validi pronti da consegnare al portale. La connessione richiede account + credenziali del portale
        (BASIC/API key, IP whitelist per Immobiliare real-time) · vedi docs/portali-integrazione.md
      </p>
    </div>
  );
}

function PortalCard({ p }: { p: PortalReadiness }): JSX.Element {
  const allReady = p.total > 0 && p.missing === 0;
  return (
    <div className="ticks relative rounded-[5px] border border-rule bg-surface">
      <div className="flex flex-wrap items-center gap-[10px] border-b border-rule p-[13px_16px]">
        {p.foreign ? <Globe className="size-4 text-accent" /> : <Radio className="size-4 text-accent" />}
        <h3 className="text-[14px] font-semibold text-ink">{p.name}</h3>
        <span className="font-mono text-[11px] text-ink-mute">{p.note}</span>
        <span className="rounded-pill border border-rule bg-surface-2 px-2.5 py-0.5 font-mono text-[10.5px] uppercase tracking-wider text-ink-mute">
          {p.format === "kyero" ? "Kyero v3 · intl" : "Immobiliare-style"}
        </span>
        <span className="ml-auto inline-flex items-center gap-[7px] rounded-full border border-rule bg-surface-2 px-[10px] py-[3px] text-[11.5px] text-ink-soft">
          {allReady ? <CheckCircle2 className="size-3.5 text-success" /> : <span className="size-[6px] rounded-full bg-warn" />}
          {p.ready}/{p.total} pronti
        </span>
      </div>
      <div className="space-y-3 p-[16px_18px]">
        <Permalink url={`${API_BASE}${p.feed_path}`} />
        {p.missing > 0 ? (
          <div className="rounded-[5px] border border-warn/40 bg-warn/10 p-[12px_14px]">
            <div className="flex items-center gap-2 text-[12.5px] font-semibold text-ink">
              <AlertTriangle className="size-4 text-warn" /> {p.missing} immobili incompleti per {p.name}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {p.incomplete.map((l) => (
                <Link
                  key={l.id}
                  to={`/listings/${l.id}`}
                  title={`Mancano: ${l.missing.join(", ")}`}
                  className="rounded-[4px] border border-rule bg-surface-2 px-2 py-1 text-[12px] text-ink-soft hover:border-accent hover:text-ink"
                >
                  {l.title} <span className="text-ink-mute">· manca {l.missing.join(", ")}</span>
                </Link>
              ))}
              {p.missing > p.incomplete.length ? (
                <span className="rounded-[4px] px-2 py-1 text-[12px] text-ink-mute">+{p.missing - p.incomplete.length} altri</span>
              ) : null}
            </div>
          </div>
        ) : p.total === 0 ? (
          <p className="micro">Nessun immobile sindacato su questo portale.</p>
        ) : (
          <p className="text-[12.5px] text-ink-soft">Tutti gli immobili sindacati sono completi per questo portale.</p>
        )}
      </div>
      <div className="border-t border-rule-soft p-[10px_16px]">
        <span className="inline-flex items-center gap-[7px] text-[11.5px] text-ink-soft">
          <span className="size-[6px] rounded-full bg-warn" /> account non collegato · gestito dall'agenzia
        </span>
      </div>
    </div>
  );
}

function Stat({ v, l, last }: { v: string; l: string; last?: boolean }): JSX.Element {
  return (
    <div className={`p-[16px_18px] ${last ? "" : "border-r border-rule-soft"}`}>
      <div className="font-mono text-[25px] font-semibold tracking-tight text-ink">{v}</div>
      <div className="micro mt-[5px]">{l}</div>
    </div>
  );
}
